import { firestore } from '../firebase';
import { doc as fsDoc, runTransaction as fsRunTransaction } from 'firebase/firestore';

// Atomic receipt-number generator. Format: RCP-YY-NNNN per cityCode.
// Mirror of QT-26-0001 style account counters.
export async function generateReceiptNumber(cityCode) {
  const code = (cityCode || 'XX').toString().toUpperCase().slice(0, 4);
  let receiptNum = 1;
  await fsRunTransaction(firestore, async (t) => {
    const counterRef = fsDoc(firestore, 'receiptCounters', code);
    const snap = await t.get(counterRef);
    receiptNum = (snap.exists() ? (Number(snap.data()?.count) || 0) : 0) + 1;
    t.set(counterRef, { count: receiptNum });
  });
  const yy = new Date().getFullYear().toString().slice(-2);
  return `RCP-${yy}-${String(receiptNum).padStart(4, '0')}`;
}

export function fmtMoney(n) {
  return `Rs. ${Number(n || 0).toLocaleString('en-PK')}`;
}

export function toIntl(phone) {
  const clean = (phone || '').replace(/[\s\-()]/g, '');
  return clean.startsWith('0') ? '92' + clean.slice(1) : clean;
}

export function mergerLine(source) {
  return '\n\nMazeed Updates ya Information k liye hamara WhatsApp Channel Follow krain *https://whatsapp.com/channel/0029Vb95BjuICVfqi5g7Ji1v* ya visit karain www.mobilistan.pk';
}

export function cityCodeFromCity(city) {
  return (city || 'XX').trim().slice(0, 3).toUpperCase() || 'XX';
}

// Today's date key in YYYY-MM-DD (Karachi-friendly: local browser tz is fine for daily grouping)
export function todayKey() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Karachi' });
}

export function monthKey(d = new Date()) {
  return new Date(d).toLocaleDateString('en-CA', { timeZone: 'Asia/Karachi' }).slice(0, 7);
}

// Compress an image File to <= maxBytes using a canvas. Returns a Blob (jpeg).
// Falls back to original if it cannot reduce further.
export async function compressImage(file, maxBytes = 200 * 1024, maxDim = 1600) {
  if (!file) return null;
  if (!(file.type || '').startsWith('image/')) return file;
  const dataUrl = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
  const img = await new Promise((resolve, reject) => {
    const im = new Image();
    im.onload = () => resolve(im);
    im.onerror = reject;
    im.src = dataUrl;
  });
  let { width, height } = img;
  if (width > maxDim || height > maxDim) {
    const r = Math.min(maxDim / width, maxDim / height);
    width = Math.round(width * r);
    height = Math.round(height * r);
  }
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, width, height);

  let q = 0.92;
  let blob = await canvasToBlob(canvas, q);
  while (blob && blob.size > maxBytes && q > 0.3) {
    q -= 0.1;
    blob = await canvasToBlob(canvas, q);
  }
  // Still too big? Shrink dimensions.
  let scale = 0.85;
  while (blob && blob.size > maxBytes && scale > 0.3) {
    const c2 = document.createElement('canvas');
    c2.width = Math.round(width * scale);
    c2.height = Math.round(height * scale);
    c2.getContext('2d').drawImage(canvas, 0, 0, c2.width, c2.height);
    blob = await canvasToBlob(c2, 0.8);
    scale -= 0.1;
  }
  return blob || file;
}

function canvasToBlob(canvas, quality) {
  return new Promise(resolve => canvas.toBlob(b => resolve(b), 'image/jpeg', quality));
}

// Upload a Blob to Firebase Storage and return the download URL.
// Imported dynamically so the storage SDK only loads when an upload happens.
export async function uploadCashAttachment(blob, path) {
  if (!blob) return null;
  const [{ getStorage, ref: sref, uploadBytes, getDownloadURL }, appMod] = await Promise.all([
    import('firebase/storage'),
    import('../firebase'),
  ]);
  const storage = getStorage(appMod.default);
  const fileRef = sref(storage, path);
  await uploadBytes(fileRef, blob, { contentType: blob.type || 'image/jpeg' });
  return await getDownloadURL(fileRef);
}

// Grace period in days before a slot is considered overdue
export const GRACE_DAYS = 5;

// Returns true if a single schedule slot is past its due date PLUS the grace
// period AND has not been fully paid. Returns false for paid slots, future
// slots, and slots within the grace window.
export const isSlotOverdue = (slot) => {
  if (!slot) return false;
  const paid = Number(slot.paidAmount || 0);
  const due  = Number(slot.dueAmount || 0);
  if (paid >= due) return false;
  if (!slot.dueDate) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dueDate = new Date(slot.dueDate);
  if (isNaN(dueDate.getTime())) return false;
  dueDate.setHours(0, 0, 0, 0);
  const graceEnd = new Date(dueDate);
  graceEnd.setDate(graceEnd.getDate() + GRACE_DAYS);
  return today > graceEnd;
};

// Returns total overdue amount across an entire product schedule, honouring
// the grace period.
export const getTotalOverdueAmount = (schedule) => {
  return Object.values(schedule || {}).reduce((sum, slot) => {
    if (isSlotOverdue(slot)) {
      return sum + (Number(slot.dueAmount || 0) - Number(slot.paidAmount || 0));
    }
    return sum;
  }, 0);
};
